import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { Card } from "react-bootstrap";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";
import { getIndicators } from "../services/api";
import "../styles/IndicatorsChart.css";

ChartJS.register(LineElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

const MAX_HISTORY_POINTS = 30;
const AUTO_REFRESH_MS = 10000;

const formatSymbol = (value) => {
  const raw = String(value || "DASH_USDT").toUpperCase();
  if (raw.includes("_")) return raw;
  return raw.replace("USDT", "_USDT");
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatCurrency = (value, digits = 4) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : `$${numeric.toFixed(digits)}`;
};

const formatSigned = (value, digits = 4) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(digits)}`;
};

const formatSignedPercent = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(digits)}%`;
};

const IndicatorsChart = ({
  symbol = "DASH_USDT",
  refreshTrigger = 0,
  manualRefreshTrigger = 0,
  availablePairs = [],
  onSymbolChange,
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const currentSymbol = useMemo(() => formatSymbol(symbol), [symbol]);
  const lastRefreshRef = useRef(0);
  const requestSeqRef = useRef(0);
  const currentSymbolRef = useRef("");

  useEffect(() => {
    currentSymbolRef.current = currentSymbol;
    setHistory([]);
    setError(null);
    setLoading(true);
  }, [currentSymbol]);

  const fetchChartData = useCallback(async () => {
    const requestSymbol = currentSymbol;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;

    try {
      const indicators = await getIndicators(requestSymbol);
      if (requestSeq !== requestSeqRef.current || requestSymbol !== currentSymbolRef.current) {
        return;
      }
      const close = toNumber(indicators?.close);

      if (close === null) {
        throw new Error("Indicadores sin precio válido (close)");
      }

      setHistory((prev) => {
        const next = [...prev, { ...indicators, close }];
        if (next.length > MAX_HISTORY_POINTS) next.shift();
        return next;
      });

      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      if (requestSeq !== requestSeqRef.current || requestSymbol !== currentSymbolRef.current) {
        return;
      }
      console.error("❌ Error al obtener gráfico:", err);
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo cargar el gráfico";
      setError(message);
    } finally {
      if (requestSeq === requestSeqRef.current && requestSymbol === currentSymbolRef.current) {
        setLoading(false);
      }
    }
  }, [currentSymbol]);

  useEffect(() => {
    fetchChartData();
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchChartData();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchChartData]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      const now = Date.now();
      if (now - lastRefreshRef.current < AUTO_REFRESH_MS * 0.8) return;
      lastRefreshRef.current = now;
      fetchChartData();
    }
  }, [refreshTrigger, fetchChartData]);

  useEffect(() => {
    if (manualRefreshTrigger > 0) {
      lastRefreshRef.current = Date.now();
      fetchChartData();
    }
  }, [manualRefreshTrigger, fetchChartData]);

  const stats = useMemo(() => {
    if (history.length === 0) return null;

    const prices = history.map((item) => item.close);
    const current = prices[prices.length - 1];
    const first = prices[0];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const delta = current - first;
    const deltaPercent = first > 0 ? (delta / first) * 100 : 0;

    return {
      current,
      min,
      max,
      delta,
      deltaPercent,
      positive: delta >= 0,
    };
  }, [history]);

  const chartData = useMemo(() => {
    if (!stats) return { labels: [], datasets: [] };

    const lineColor = stats.positive ? "#53ffc0" : "#ff8e8e";
    const fillColor = stats.positive ? "rgba(83, 255, 192, 0.18)" : "rgba(255, 142, 142, 0.18)";

    return {
      labels: history.map((_, index) => {
        const age = history.length - index;
        return age === 1 ? "Ahora" : `hace ${age * 5}s`;
      }),
      datasets: [
        {
          label: "Precio (USDT)",
          data: history.map((item) => item.close),
          borderColor: lineColor,
          backgroundColor: fillColor,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: lineColor,
          pointBorderColor: "#fff",
          pointBorderWidth: 1.5,
          borderWidth: 2,
          tension: 0.35,
        },
      ],
    };
  }, [history, stats]);

  const chartOptions = useMemo(() => {
    const firstPrice = history[0]?.close ?? null;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: "#95aebb",
            font: { size: 11, weight: "700" },
            padding: 12,
            usePointStyle: true,
          },
        },
        tooltip: {
          backgroundColor: "rgba(2, 10, 20, 0.94)",
          titleColor: "#5bffc8",
          bodyColor: "#f0fbff",
          borderColor: "rgba(83, 255, 192, 0.3)",
          borderWidth: 1,
          callbacks: {
            title: (context) => `⏰ ${context[0].label}`,
            label: (context) => {
              const value = toNumber(context.raw) ?? 0;
              const diff = firstPrice !== null ? value - firstPrice : 0;
              const diffPct = firstPrice && firstPrice > 0 ? (diff / firstPrice) * 100 : 0;

              return [
                `💰 Precio: $${value.toFixed(4)} USDT`,
                `${diff >= 0 ? "📈" : "📉"} ${formatSigned(diff, 4)} (${formatSignedPercent(diffPct, 2)})`,
              ];
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          grid: {
            color: "rgba(0, 255, 170, 0.08)",
            drawBorder: false,
          },
          ticks: {
            color: "#8ea5b2",
            font: { size: 10 },
            callback: (value) => `$${Number(value).toFixed(2)}`,
          },
        },
        x: {
          grid: {
            color: "rgba(0, 255, 170, 0.05)",
            drawBorder: false,
          },
          ticks: {
            color: "#8ea5b2",
            font: { size: 10 },
            maxRotation: 45,
            minRotation: 0,
          },
        },
      },
    };
  }, [history]);

  if (error && history.length === 0) {
    return (
      <Card className="ind-chart-card">
        <Card.Header className="ind-chart-header">
          <h5 className="ind-chart-title">📊 Evolución del Precio - {currentSymbol}</h5>
        </Card.Header>

        <Card.Body className="ind-chart-body">
          <div className="ind-chart-state ind-chart-state-error">
            <p>❌ {error}</p>
            <button type="button" onClick={fetchChartData} className="ind-chart-btn">
              🔄 Reintentar
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (loading && history.length === 0) {
    return (
      <Card className="ind-chart-card">
        <Card.Header className="ind-chart-header">
          <h5 className="ind-chart-title">📊 Evolución del Precio - {currentSymbol}</h5>
        </Card.Header>

        <Card.Body className="ind-chart-body">
          <div className="ind-chart-state">
            <div className="ind-chart-spinner" />
            <p>⏳ Cargando gráfico...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="ind-chart-card">
      <Card.Header className="ind-chart-header">
        <div className="ind-chart-header-row">
          <h5 className="ind-chart-title">📊 Evolución del Precio - {currentSymbol.replace("_", "/")}</h5>
          {availablePairs.length > 1 ? (
            <select
              className="indicator-symbol-select"
              value={currentSymbol}
              onChange={(event) => onSymbolChange?.(event.target.value)}
            >
              {availablePairs.map((pair) => (
                <option key={`${pair.market_type}:${pair.symbol}`} value={pair.symbol}>
                  {pair.symbol} · {pair.market_type === "FUTURES" ? "Futuros" : "Spot"}
                </option>
              ))}
            </select>
          ) : null}
          <span className="ind-chart-time">{lastUpdate ? `Actualizado: ${lastUpdate.toLocaleTimeString()}` : "Actualizando..."}</span>
        </div>
      </Card.Header>

      <Card.Body className="ind-chart-body">
        {stats && (
          <div className="ind-chart-stats-grid">
            <article className="ind-chart-stat-card highlight">
              <span className="ind-chart-stat-label">💰 Precio actual</span>
              <strong className="ind-chart-stat-value">{formatCurrency(stats.current, 4)}</strong>
              <small className="ind-chart-stat-hint">Última lectura</small>
            </article>

            <article className="ind-chart-stat-card">
              <span className="ind-chart-stat-label">📉 Mínimo</span>
              <strong className="ind-chart-stat-value">{formatCurrency(stats.min, 4)}</strong>
              <small className="ind-chart-stat-hint">Ventana reciente</small>
            </article>

            <article className="ind-chart-stat-card">
              <span className="ind-chart-stat-label">📈 Máximo</span>
              <strong className="ind-chart-stat-value">{formatCurrency(stats.max, 4)}</strong>
              <small className="ind-chart-stat-hint">Ventana reciente</small>
            </article>

            <article className={`ind-chart-stat-card ${stats.positive ? "positive" : "negative"}`}>
              <span className="ind-chart-stat-label">{stats.positive ? "📈 Cambio" : "📉 Cambio"}</span>
              <strong className="ind-chart-stat-value">{formatSigned(stats.delta, 4)}</strong>
              <small className="ind-chart-stat-hint">{formatSignedPercent(stats.deltaPercent, 2)}</small>
            </article>
          </div>
        )}

        <div className="ind-chart-plot-wrap">
          {chartData.labels.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <div className="ind-chart-empty">⏳ Esperando datos del gráfico...</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default React.memo(IndicatorsChart);
